import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader, RefreshCw } from 'lucide-react';
import { JobType, type WorkflowJob } from '../graphql/types';
import * as jobTypeConfigService from '../services/jobTypeConfigService';
import { WorkspaceRoute } from './WorkspaceRoute';
import { WorkflowRail } from './WorkflowRail';
import { useWorkflowJob } from './useWorkflowJob';
import { useWorkflowData } from './useWorkflowData';
import type { WorkflowDataState } from './useWorkflowData';
import type { AnalyzeResult } from './analyze/analyzeModel';
import {
  getWorkflowModeStatusPill,
  getWorkflowTitle,
  type WorkflowStatusContext,
} from './workflowStatus';
import { getDefaultMode, getLockedModes, readJobWorkspaceParams } from './rowMode';
import {
  SELECTION_PARAM_KEYS,
  writeWorkspaceParams,
  type SelectionParamKey,
  type WorkspaceMode,
} from './urlState';

/**
 * Selection carried in the URL (`loan`, `doc`, `field`, `exception`). It is
 * independent of the Mode, so it survives Mode changes (Requirement 3.4).
 */
export type WorkspaceSelection = Partial<Record<SelectionParamKey, string>>;

export interface WorkflowWorkspaceContextValue {
  job: WorkflowJob;
  jobType: JobType.DD | JobType.BA;
  /**
   * The Mode being shown: the URL `mode`, or the Row → Mode default for the
   * job's stage when the URL has none.
   */
  mode: WorkspaceMode;
  /** Re-fetch the job; resolves when the load has finished. */
  reload: () => Promise<void>;
  /** Replace the job with a fresher copy from a child component. */
  updateJob: (job: WorkflowJob) => void;
  selection: WorkspaceSelection;
  /** Change selection keys only; the Mode and unrelated params are kept. */
  select: (update: Partial<Record<SelectionParamKey, string | null>>) => void;
  /**
   * Report counts (files, documents, confirmed fields, open findings) and the
   * BA borrower and loan number so the Top_Bar can show them. Unknown values
   * keep the stage wording. Safe to call from effects: unchanged values do not
   * re-render.
   */
  reportStatus: (update: WorkflowStatusContext) => void;
  /**
   * Loans, files and documents of the job, loaded once for the Workspace. The
   * Rail and Extract read it from here.
   */
  data: WorkflowDataState;
  /**
   * Re-fetch `data`. Bodies that change loans or documents (Intake) call it so
   * the Rail follows. The default is a quiet refresh.
   */
  refreshData: (showSpinner?: boolean) => Promise<void>;
  /** Changes whenever field confirmations are saved, so counts can be re-read. */
  confirmationsVersion: number;
  notifyConfirmationsChanged: () => void;
  /**
   * Findings of the last Analyze run, reported by Analyze so the Rail can show
   * the plain-language status line of each loan in every Mode.
   */
  analysis: AnalyzeResult | null;
  reportAnalysis: (analysis: AnalyzeResult | null) => void;
}

const WorkflowWorkspaceContext = createContext<WorkflowWorkspaceContextValue | null>(null);

/** Job, loader actions and selection for the Mode bodies of a DD/BA Workspace. */
export function useWorkflowWorkspace(): WorkflowWorkspaceContextValue {
  const ctx = useContext(WorkflowWorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkflowWorkspace must be used inside <WorkflowWorkspace>');
  }
  return ctx;
}


export interface WorkflowWorkspaceProps {
  jobId: string;
  jobType: JobType.DD | JobType.BA;
  /** Mode body; reads the job through `useWorkflowWorkspace()`. */
  children: React.ReactNode;
}

/**
 * DD/BA Workspace container. Owns job loading, the `onJobStatusChanged`
 * subscription and the polling fallback (`useWorkflowJob`), feeds the Top_Bar
 * the real title and Status_Pill text, and hands the job and URL selection to
 * the body through context. It also loads the job's loans, files and documents
 * once (`useWorkflowData`) and shows the one Rail (`WorkflowRail`) beside every
 * Mode body.
 *
 * Requirements: 3.4, 3.6.
 */
export const WorkflowWorkspace: React.FC<WorkflowWorkspaceProps> = ({
  jobId,
  jobType,
  children,
}) => {
  const { job, loading, error, reload, updateJob } = useWorkflowJob(jobId, jobType);
  const [searchParams, setSearchParams] = useSearchParams();
  const config = jobTypeConfigService.getConfig(jobType);

  // Row → Mode: a link without `mode` opens the Mode that matches the job's
  // stage (Requirement 2.6).
  const params = useMemo(
    () => readJobWorkspaceParams(searchParams, jobType, job, { isDraft: false }),
    [searchParams, jobType, job]
  );
  const mode = params.mode;
  const locked = useMemo(() => (job ? getLockedModes(jobType, job) : undefined), [jobType, job]);

  const selection = useMemo<WorkspaceSelection>(() => {
    const result: WorkspaceSelection = {};
    for (const key of SELECTION_PARAM_KEYS) {
      if (params[key]) result[key] = params[key];
    }
    return result;
  }, [params]);

  const select = useCallback(
    (update: Partial<Record<SelectionParamKey, string | null>>) => {
      setSearchParams(writeWorkspaceParams(searchParams, update));
    },
    [searchParams, setSearchParams]
  );

  // Counts reported by the Mode bodies and the Rail for the Top_Bar wording.
  const [statusContext, setStatusContext] = useState<WorkflowStatusContext>({});
  const reportStatus = useCallback((update: WorkflowStatusContext) => {
    setStatusContext((prev) => {
      const keys = Object.keys(update) as Array<keyof WorkflowStatusContext>;
      return keys.every((k) => prev[k] === update[k]) ? prev : { ...prev, ...update };
    });
  }, []);

  const status = job ? getWorkflowModeStatusPill(job, mode, statusContext) : undefined;
  const title = getWorkflowTitle(job, config.displayName, statusContext);

  // Loans, files and documents are loaded once here, so the Rail is the same in
  // every Mode and Extract does not load them again. They refresh quietly when
  // the job moves on or the Mode changes.
  const dataRefreshKey = `${job?.status}|${job?.processedLoanCount}|${job?.loanCount}|${mode}`;
  const { state: data, refresh: refreshData } = useWorkflowData(jobId, !!job, dataRefreshKey);

  const [confirmationsVersion, setConfirmationsVersion] = useState(0);
  const notifyConfirmationsChanged = useCallback(() => setConfirmationsVersion((n) => n + 1), []);

  // Last Analyze result; it belongs to one job.
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const reportAnalysis = useCallback(
    (next: AnalyzeResult | null) => setAnalysis((prev) => (prev === next ? prev : next)),
    []
  );
  useEffect(() => setAnalysis(null), [jobId]);

  const contextValue = useMemo<WorkflowWorkspaceContextValue | null>(
    () =>
      job
        ? {
            job,
            jobType,
            mode,
            reload,
            updateJob,
            selection,
            select,
            reportStatus,
            data,
            refreshData,
            confirmationsVersion,
            notifyConfirmationsChanged,
            analysis,
            reportAnalysis,
          }
        : null,
    [
      job,
      jobType,
      mode,
      reload,
      updateJob,
      selection,
      select,
      reportStatus,
      data,
      refreshData,
      confirmationsVersion,
      notifyConfirmationsChanged,
      analysis,
      reportAnalysis,
    ]
  );

  let body: React.ReactNode;
  if (error) {
    body = (
      <div className="p-6">
        <div role="alert" className="max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 flex-shrink-0 text-red-500" aria-hidden="true" />
            <div className="ml-4 flex-1">
              <h2 className="text-lg font-medium text-red-800">Error loading job</h2>
              <p className="mt-2 text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => void reload()}
                className="mt-4 inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!contextValue) {
    // Show the spinner only while there is no job to show. Reloads after a step
    // completes keep the current body mounted so its local state survives.
    body = loading ? (
      <div className="flex min-h-[400px] flex-col items-center justify-center">
        <Loader className="mb-4 h-12 w-12 animate-spin text-[#834DFB]" aria-hidden="true" />
        <p className="text-gray-600">Loading job data...</p>
      </div>
    ) : (
      <div className="py-12 text-center">
        <p className="text-gray-600">Job not found.</p>
      </div>
    );
  } else {
    body = <div className="p-6">{children}</div>;
  }

  // One Rail on every Mode: the loans (DD) or the borrower's documents (BA).
  const rail = contextValue ? <WorkflowRail /> : undefined;

  return (
    <WorkflowWorkspaceContext.Provider value={contextValue}>
      <WorkspaceRoute
        feature={jobType}
        itemId={jobId}
        title={title}
        statusLabel={status?.label}
        statusTone={status?.tone}
        defaultMode={job ? getDefaultMode(job) : undefined}
        locked={locked}
        rail={rail}
        railLabel={jobType === JobType.BA ? 'Documents' : 'Loans'}
      >
        {body}
      </WorkspaceRoute>
    </WorkflowWorkspaceContext.Provider>
  );
};

export default WorkflowWorkspace;
